<script>
import { toRef, watch, onBeforeUnmount, nextTick } from 'vue';
import { isEmptyObject } from '../../../../helper/commons';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import IntegrationDialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import NewHook from './NewHook.vue';
import SingleIntegrationHooks from './SingleIntegrationHooks.vue';
import MultipleIntegrationHooks from './MultipleIntegrationHooks.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

export default {
  components: {
    IntegrationDialog,
    NewHook,
    SingleIntegrationHooks,
    MultipleIntegrationHooks,
    SettingsLayout,
    BaseSettingsHeader,
  },
  props: {
    integrationId: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props) {
    const { isWorkspaceV2 } = useWorkspaceV2();
    const store = useStore();
    const { accountId } = useAccount();
    let contextVersion = 0;
    watch(
      () => props.integrationId,
      () => {
        contextVersion += 1;
      },
      { flush: 'sync' }
    );
    watch(
      accountId,
      (next, prev) => {
        if (!next || !prev || String(next) === String(prev)) return;
        contextVersion += 1;
        store.dispatch('integrations/get');
      },
      { flush: 'sync' }
    );
    onBeforeUnmount(() => {
      contextVersion += 1;
    });
    const captureContext = () => {
      const version = contextVersion;
      return () => version === contextVersion;
    };
    const {
      integration,
      isIntegrationMultiple,
      isIntegrationSingle,
      isHookTypeInbox,
    } = useIntegrationHook(toRef(props, 'integrationId'));

    return {
      captureContext,
      accountId,
      isWorkspaceV2,
      integration,
      isIntegrationMultiple,
      isIntegrationSingle,
      isHookTypeInbox,
    };
  },
  data() {
    return {
      loading: {},
      showAddHookModal: false,
      showDeleteConfirmationPopup: false,
      selectedHook: {},
      alertMessage: '',
      deleting: false,
      formSubmitting: false,
      deleteError: '',
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'integrations/getUIFlags' }),
    showIntegrationHooks() {
      return !this.uiFlags.isFetching && !isEmptyObject(this.integration);
    },
    showAddButton() {
      return this.showIntegrationHooks && this.isIntegrationMultiple;
    },
    deleteTitle() {
      return this.isHookTypeInbox
        ? this.$t('INTEGRATION_APPS.DELETE.TITLE.INBOX')
        : this.$t('INTEGRATION_APPS.DELETE.TITLE.ACCOUNT');
    },
    deleteMessage() {
      return this.isHookTypeInbox
        ? this.$t('INTEGRATION_APPS.DELETE.MESSAGE.INBOX')
        : this.$t('INTEGRATION_APPS.DELETE.MESSAGE.ACCOUNT');
    },
    confirmText() {
      return this.isHookTypeInbox
        ? this.$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.INBOX')
        : this.$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.ACCOUNT');
    },
    cancelText() {
      return this.$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT');
    },
  },
  watch: {
    showAddHookModal() {
      if (!this.isWorkspaceV2) return;
      nextTick(() => {
        if (this.showAddHookModal) this.$refs.addDialog?.open();
        else this.$refs.addDialog?.close();
      });
    },
    showDeleteConfirmationPopup() {
      if (!this.isWorkspaceV2) return;
      nextTick(() => {
        if (this.showDeleteConfirmationPopup) this.$refs.deleteDialog?.open();
        else this.$refs.deleteDialog?.close();
      });
    },
    integrationId() {
      this.resetPanel();
    },
    accountId(next, prev) {
      if (!next || !prev || String(next) === String(prev)) return;
      this.resetPanel();
    },
  },
  mounted() {
    this.$store.dispatch('integrations/get');
  },
  methods: {
    resetPanel() {
      this.formSubmitting = false;
      this.deleting = false;
      this.showAddHookModal = false;
      this.showDeleteConfirmationPopup = false;
      this.selectedHook = {};
      this.deleteError = '';
    },
    openAddHookModal() {
      this.showAddHookModal = true;
    },
    hideAddHookModal() {
      if (this.formSubmitting) return;
      this.showAddHookModal = false;
    },
    openDeletePopup(response) {
      this.deleteError = '';
      this.showDeleteConfirmationPopup = true;
      this.selectedHook = response;
    },
    closeDeletePopup() {
      if (this.deleting) return;
      this.showDeleteConfirmationPopup = false;
    },
    async confirmDeletion() {
      if (this.deleting) return;
      const isCurrent = this.captureContext();
      this.deleting = true;
      this.deleteError = '';
      try {
        await this.$store.dispatch('integrations/deleteHook', {
          hookId: this.selectedHook.id,
          appId: this.selectedHook.app_id,
        });
        if (!isCurrent()) return;
        this.alertMessage = this.$t(
          'INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE'
        );
        this.deleting = false;
        this.closeDeletePopup();
      } catch (error) {
        if (!isCurrent()) return;
        const errorMessage = error?.response?.data?.message;
        this.alertMessage =
          errorMessage || this.$t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE');
        this.deleteError = this.alertMessage;
      } finally {
        if (isCurrent()) {
          this.deleting = false;
          useAlert(this.alertMessage);
        }
      }
    },
  },
};
</script>

<template>
  <SettingsLayout
    data-testid="integration-hooks"
    :is-loading="uiFlags.isFetching"
  >
    <template v-if="isIntegrationSingle" #header>
      <BaseSettingsHeader
        :title="integration.name || ''"
        description=""
        :feature-name="integrationId"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div v-if="showIntegrationHooks" class="w-full">
        <div v-if="isIntegrationMultiple">
          <MultipleIntegrationHooks
            :integration-id="integrationId"
            :show-add-button="showAddButton"
            @add="openAddHookModal"
            @delete="openDeletePopup"
          />
        </div>

        <div v-if="isIntegrationSingle">
          <SingleIntegrationHooks
            :integration-id="integrationId"
            @add="openAddHookModal"
            @delete="openDeletePopup"
          />
        </div>
      </div>
    </template>
    <IntegrationDialog
      v-if="isWorkspaceV2"
      ref="addDialog"
      content-tag="div"
      :title="integration.name"
      :show-confirm-button="false"
      :show-cancel-button="false"
      confirm-before-close
      @request-close="hideAddHookModal"
      @close="showAddHookModal = false"
    >
      <NewHook
        :key="`${accountId}-${integrationId}`"
        :integration-id="integrationId"
        embedded
        @submitting-change="formSubmitting = $event"
        @close="hideAddHookModal"
      />
    </IntegrationDialog>
    <woot-modal
      v-else
      v-model:show="showAddHookModal"
      :on-close="hideAddHookModal"
    >
      <NewHook
        :key="`${accountId}-${integrationId}`"
        :integration-id="integrationId"
        @submitting-change="formSubmitting = $event"
        @close="hideAddHookModal"
      />
    </woot-modal>

    <IntegrationDialog
      v-if="isWorkspaceV2"
      ref="deleteDialog"
      type="alert"
      :title="deleteTitle"
      :description="deleteMessage"
      :confirm-button-label="confirmText"
      :cancel-button-label="cancelText"
      :is-loading="deleting"
      confirm-before-close
      @confirm="confirmDeletion"
      @request-close="closeDeletePopup"
      @close="showDeleteConfirmationPopup = false"
    >
      <p v-if="deleteError" role="alert" class="text-sm text-n-ruby-11">
        {{ deleteError }}
      </p>
    </IntegrationDialog>
    <woot-delete-modal
      v-else
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="deleteTitle"
      :message="deleteMessage"
      :error-message="deleteError"
      :confirm-text="confirmText"
      :reject-text="cancelText"
    />
  </SettingsLayout>
</template>
